/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faSend } from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import {
    ButtonProps,
    FormGroup, Input,
    Modal, ModalBody, ModalFooter, ModalHeader, Row
} from "reactstrap";
import usePatchLayer from "../../../../hooks/usePatchLayer";
import { blobToBase64 } from "../../../../utils/base64";
import { QC_STATUS } from "./Report/QCStatusButton";


import useConfig from "../../../../actions/Tenants/config/configHook";
import { breakpoints } from "../../../../constants/mediaqueries";
import useAvosApi from "../../../../utils/useAvosApiHook";
import { AttachmentLink } from "../../../Helper/AttachmentLink";
import { Button } from "../../../Helper/Button";
import { getBlobNameFromUrl } from "../../Layers/Layer/LayerReportsView";
import { truncateText } from "../../Layers/Layer/Report/utils";
import { getUploadedReports } from "./LayerReportsView";


import { useRouteLayer } from "../../../../hooks/useCurrentEntities";

export interface AttachedUrls {
    [key: string]: string;
}

export interface SendReportRequestBody {
    layer_id: string;
    report_base64: string;
    config_value: string;
    report_file_type: string;
    attached_urls?: AttachedUrls;
}

export interface SendReportButtonProps extends ButtonProps {
    blob: Blob | undefined;
    layerId: string;
    config_value: string;
    status: string;
}

export const SendReportButton = ({ layerId, blob, config_value, status, ...buttonProps }: SendReportButtonProps) => {
    const patchLayer = usePatchLayer();
    const layer = useRouteLayer();

    const [onSendReport] = useAvosApi("/report/send", "POST", {}, {
        onSuccess: {
            message: "Report sending request received",
            showMessage: true
        }
    }) as any;
    const onSend = async (attached_urls) => {
        // * Update the layer QC status to sent
        patchLayer(layer, `qc_status_${config_value}`, QC_STATUS.SENT, true);

        const base64Data = await blobToBase64(blob);

        onSendReport({
            layer_id: layerId,
            report_base64: base64Data,
            config_value,
            status, // * Actual result of the report success, warning, danger
            report_file_type: "pdf",
            attached_urls,
        });
    };

    const pdfSizeInMB = blob ? blob.size / 1024 / 1024 : 0;
    return <SendReportModal onSend={onSend} config_value={config_value} pdfSizeInMB={pdfSizeInMB} {...buttonProps} />;
};


const MAX_PDF_SIZE_IN_MB = 8;


interface SendReportModalProps extends ButtonProps{
    onSend: (attachedUrls?: AttachedUrls) => void;
    config_value: string;
    pdfSizeInMB: number;
    checkPDFSize?: boolean;
}

export const SendReportModal = ({ onSend, config_value, pdfSizeInMB, checkPDFSize, ...buttonProps }: SendReportModalProps) => {
    const config = useConfig();
    const layer = useRouteLayer();
    const [attachedUrls, setAttachedUrls] = useState({});
    const uploads = getUploadedReports(layer);
    const layer_config = config.get_layer_config(layer);
    const pdf_config = config.get_pdf_report(layer, config_value);

    const [isOpen, setIsOpen] = useState(false);
    const onSendButtonClick = () => {
        setIsOpen(false);
        onSend(attachedUrls);
    };
    const handleCheckboxChange = (key, index, url) => {
        const blob_name = getBlobNameFromUrl(url);
        setAttachedUrls((prevState) => ({
            ...prevState,
            [`${key}_${index}`]: !prevState[`${key}_${index}`] ? blob_name : undefined
        }));
    };

    const exceedsThePdfSizeLimit = pdfSizeInMB > MAX_PDF_SIZE_IN_MB;
    const isPdfEmpty = pdfSizeInMB === 0;
    const sendReportButtonDisabled = checkPDFSize && (isPdfEmpty || exceedsThePdfSizeLimit);

    // * For every report type that is uploadable I will also assume you can add it as an attachement
    // const attachedReports = Object.entries(uploads).filter(([reportType]) => pdf_config.attachements.include(reportType) );
    const attachedReports = Object.entries(uploads);
    const audienceLabel = pdf_config?.audience?.map((key) => key.replace(/_/g, " ")).join(", ") || "internal users";
    return <>
        <div className={`d-flex flex-column-reverse flex-sm-row flex-wrap gap-2 align-items-start align-items-sm-end justify-content-sm-${checkPDFSize && exceedsThePdfSizeLimit ? "between" : "end"}`} css={css`@media (max-width: ${breakpoints[1]}px) { width: ${checkPDFSize && exceedsThePdfSizeLimit ? "100%" : "auto"}; }`}>
            {(checkPDFSize && exceedsThePdfSizeLimit) && (
                <span className="text-warning fs-6 flex-grow-1 text-md-center">The report size exceeds the limit of 8MB</span>
            )}

            <Button
                color="primary"
                outline
                disabled={sendReportButtonDisabled || pdfSizeInMB === 0}
                icon={faSend}
                {...buttonProps}
                onClick={() => setIsOpen(true)}
            >
                Send {pdf_config?.text} report
            </Button>
        </div>

        <Modal key={1} isOpen={isOpen} size="lg">
            <ModalHeader >Send {pdf_config?.text} report to the {audienceLabel}</ModalHeader>
            <ModalBody>
                <Row>{layer_config?.show_upload_reports_button && attachedReports.length > 0 && <p>Which files would you like to send?</p>}</Row>
                <Row>{layer_config?.show_upload_reports_button && attachedReports.length === 0 && <p>No attached files found.</p>}</Row>
                {attachedReports.map(([reportType, reports]) => {
                    return <div key={reportType}>
                        <div>
                            {reports.map(([key, url], index) => {
                                const target = `${key}_${index}`;
                                return (<div key={index} style={{ display: "flex", alignItems: "center" }}>
                                    <FormGroup check style={{ marginRight: "8px" }}>
                                        <Input
                                            type="checkbox"
                                            onChange={() => handleCheckboxChange(key.replace(`report_${key}_`, ""), index + 1, url)}
                                        />
                                    </FormGroup>
                                    <div style={{ flex: 1 }}>
                                        <AttachmentLink
                                            url={url}
                                            label={truncateText(key.replace(`report_${key}_`, ""), 35, 40)}
                                            id={target}
                                        />
                                    </div>
                                </div>);
                            })}

                        </div>
                    </div>;
                })}
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={() => setIsOpen(false)}>Cancel</Button>
                <Button color="success" onClick={onSendButtonClick} icon={faSend}>Send</Button>
            </ModalFooter>
        </Modal>
    </>;
};
